import React, { useState, useRef, useEffect, useCallback, RefObject, MouseEvent } from "react";
import { formatTimeMilli, FRAGMENT_MIN_HEIGHT } from "components/utils";
import {
    AudioEditWrapperSC,
    HorizontalLineSC,
    OrphanDeleteSC,
    OrphanTextSC,
    SectionMarkSC,
    SelectWrapperSC,
    TrackEmptySC,
    TrackOrphanSC,
    TrackSC,
} from "./styled";
import { orphanDeleteTitle, orphanVoicingHint } from "providers/AudioProvider/orphanVoicings";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useFolderOffsetMs } from "hooks/useFolderOffsetMs";
import { CueTime } from "components/elements/CueTime";
import { toGlobalMs } from "utils/globalTime";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { AudioPlayerTrack } from "./components/AudioPlayerTrack";
import { AudioTrack } from "providers/AudioProvider/types";
import { AudioManage } from "./components/AudioManage/AudioManage";
import { getMaxLength, getSelectedMaxMinLength } from "../../utils/drag";
import { SelectCheckbox } from "./components/SelectCheckbox";
import { SelectedTranslatedTrack } from "providers/AudioProvider/types";
import { DefaultModal } from "components/elements/DefaultModal";
import { checkSelectedTracks, manualOverlapLook, voicingWasMoved } from "./utils";
import { useNotificationContext } from "../../../../../hooks/useNotificationContext";
import { useIndexedDB } from "../../../../../hooks/useIndexedDb";
import { Database } from "../../../../../utils";
import { Correction } from "../../../../../api/fastApi/commentsTypes";
import { FAST_API_URL } from "api";
import { useRepublishSection } from "providers/RepublishSectionProvider";
import { armVoicingGate } from "utils/playbackHold";
import { seekOriginalTo } from "utils/originalMedia";
import { announceUserSeek } from "utils/playbackIntent";
import { publishClipState } from "utils/clipRegistry";
import { ClipMark } from "./components/ClipMark";
import { useUiLang } from "utils/uiLanguage";
import { cuesRu, cuesRuGen } from "utils/pluralRu";
import { voicingSounds } from "providers/AudioProvider/dubSounding";

interface DraggingSubtitleProps {
    id: string;
    ind: number;
    oneTrack: AudioTrack;
    start: number;
    length: number;
    trimmed: number | null;
    trimmed_start: number;
    tracks: AudioTrack[];
    timeoutRef: RefObject<ReturnType<typeof setTimeout> | null>;
    draggingTrack: RefObject<HTMLDivElement | null>;
    handlePlayNext: (val: number) => void;
    selectedTranslatedTrackIds: SelectedTranslatedTrack[];
    setSelectedTranslatedTrackIds: React.Dispatch<React.SetStateAction<SelectedTranslatedTrack[]>>;
    positionDiff: number;
    setPositionDiff: (val: number) => void;
    rerenderTracksIds: string[];
    setRerenderTracksIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export const DraggingTrackTranslated = React.memo(
    ({
        id,
        ind,
        oneTrack,
        start,
        length,
        trimmed,
        trimmed_start,
        tracks,
        timeoutRef,
        draggingTrack,
        handlePlayNext,
        selectedTranslatedTrackIds,
        setSelectedTranslatedTrackIds,
        positionDiff,
        setPositionDiff,
        rerenderTracksIds,
        setRerenderTracksIds,
    }: DraggingSubtitleProps) => {
        const handles = useAudioHandlesContext();
        const state = useAudioStateContext();
        const notification = useNotificationContext();
        const { deleteValue } = useIndexedDB(Database.name);

        // Блок пересборки: попадает ли эта реплика в то, что и так будет заменено, и во что
        // превратится блок, если её тронуть. Считается провайдером на весь список сразу.
        const section = useRepublishSection();
        // original_id — имя реплики на диске; id редактора несёт ещё и номер папки.
        const cueKey = oneTrack.original_id || oneTrack.id;
        const sectionState = section.rowState(cueKey);
        const widening = section.widening(cueKey);
        const sectionHint =
            sectionState === "rebuilding"
                ? "Папка сдана и пересобирается — правка приедет следующим кругом"
                : sectionState === "in"
                  ? `В блоке пересборки (${cuesRu(widening?.count ?? 0)}: ` +
                    `${formatTimeMilli(widening?.fromMs ?? 0)}–${formatTimeMilli(widening?.toMs ?? 0)}) ` +
                    "— правьте свободно, блок это не расширит"
                  : widening
                    ? `Правка расширит блок пересборки до ${cuesRuGen(widening.count)}`
                    : "";

        const zoomLevel = 100 - state["zoomLevel"];
        const folderOffsetMs = useFolderOffsetMs();
        const en = useUiLang() === "en";
        // Расстояние до следующей реплики: по нему видно, поместится ли вторая строка времени,
        // не наехав на метку соседа. Последняя реплика ограничений не имеет.
        const nextTrackStart = tracks[ind + 1]?.start;
        const gapToNextPx =
            nextTrackStart === undefined
                ? undefined
                : (nextTrackStart - oneTrack.start) / zoomLevel;
        const [position, setPosition] = useState(() => start);
        const [lengthPosition, setLengthPosition] = useState(() => length);
        const [isDragging, setIsDragging] = useState(false);
        const [isLengthing, setIsLengthing] = useState(false);
        const [isLengthingUpper, setIsLengthingUpper] = useState(false);
        const [startY, setStartX] = useState(0);
        const [lengthX, setLengthX] = useState(0);
        const [openModalText, setOpenModalText] = useState("");
        // Звук реплики кэшируется по адресу (IndexedDB), а имя wav после переозвучки то же. Без
        // версии браузер, кэшировавший прежний дубль, играл его дальше, если реплику переозвучили
        // не в этом окне (сервер, другой переводчик, вчерашний день): «Care» уже звучало верно, а
        // переводчица слышала старое «Карэ» (fdcde2a1, 01.10.2026). `file_v` — время и размер
        // файла с сервера: новый дубль — новый адрес.
        const audioUrl = (file?: string) =>
            `${FAST_API_URL}/download_rus?file_name=${encodeURIComponent(file || "")}` +
            (oneTrack.file_v && file === oneTrack["file"] ? `&v=${encodeURIComponent(oneTrack.file_v)}` : "");
        const [mp3, setMp3] = useState(() => audioUrl(oneTrack["file"]));

        const isEditing = state.editedTrackId === oneTrack.id;
        /** Эту реплику переозвучивали — значит её файл на диске уже другой, хоть и с тем же именем. */
        const wasEditingRef = useRef(false);
        useEffect(() => {
            if (isEditing) {
                // Пока реплику переозвучивают, лежащие у нас байты заведомо устарели.
                //
                // Раньше здесь ставился пустой URL — но fetch("") не падает: он возвращает
                // index.html самого редактора со статусом 200, и тот оседал в кэше как звук этой
                // реплики. Теперь, когда лекция ЖДЁТ реплику, а не бросает её через 8 секунд,
                // «вечно готовится» означало бы «лекция стоит», поэтому состояние названо прямо.
                wasEditingRef.current = true;
                publishClipState(oneTrack.id, "editing");
                deleteValue(Database.audioTable, audioUrl(oneTrack["file"]));
                return;
            }
            if (!wasEditingRef.current) return;
            wasEditingRef.current = false;
            // Переозвучка закончилась. Имя файла на диске не изменилось, а звук изменился, поэтому
            // к URL добавляется метка времени — иначе и IndexedDB, и сам элемент отдали бы старую
            // запись. Только для ЭТОЙ реплики: метка в общем случае означает новый ключ кэша, то есть
            // «качать заново», и раздавать её всей папке значило бы не кэшировать ничего.
            setMp3(`${audioUrl(oneTrack["file"])}&_t=${Date.now()}`);
            publishClipState(oneTrack.id, "idle");
        }, [isEditing]);

        useEffect(() => {
            // Перевод обновился — у реплики мог появиться другой файл. Не трогаем URL, если он уже
            // указывает на тот же файл: там может стоять метка времени после переозвучки, и сброс
            // на «чистый» адрес вернул бы из кэша прежнюю запись.
            if (!oneTrack["file"] || isEditing) return;
            const base = audioUrl(oneTrack["file"]);
            setMp3((prev) => (prev.startsWith(base) ? prev : base));
        }, [state.updatedTranslation, oneTrack.file_v]);

        useEffect(() => {
            if (rerenderTracksIds.includes(id)) {
                setPosition(start);
                setRerenderTracksIds((prev) => prev.filter((ids) => ids !== id));
            }
        }, [rerenderTracksIds]);

        useEffect(() => {
            if (
                selectedTranslatedTrackIds.find((item) => item.ind === ind) &&
                positionDiff &&
                !isDragging
            ) {
                setPosition((prev) => prev + positionDiff);
            }
        }, [positionDiff, selectedTranslatedTrackIds]);

        useEffect(() => {
            if (length !== lengthPosition) {
                setLengthPosition(length);
            }
        }, [length]);

        useEffect(() => {
            if (start !== position) {
                setPosition(start);
            }
        }, [start]);

        const clickHandler = (e?: MouseEvent<HTMLDivElement>) => {
            console.log("CLICKED = ", oneTrack);
            // STOP EVERYTHING IN THIS SAME EVENT, before the first scheduler tick.
            //
            // The writes below start the lecturer and the picture immediately; the schedule only
            // looks 25ms later. Those milliseconds are the original talking over a translation
            // that has not begun — and if the clip still has to be prepared, they are however long
            // this machine needs. The schedule replaces this arming with a real decision on its
            // next tick, and opens the gate when the clip can actually sound.
            armVoicingGate();
            // A CLICK MOVES THE PLAYHEAD. IT DOES NOT NAME THE CUE.
            //
            // It used to do both, and the two fought. Naming the cue made the schedule treat it as
            // somebody else's choice: it marked the cue played and stood down for EXPLICIT_MS,
            // during which the lecturer ran on — so by the time the schedule looked again the
            // playhead was deep inside the cue and it entered "at the matching words". Measured on
            // the deployed editor: the clicked cue started 0.782s in and was handed over three
            // times. Moving the playhead and letting the schedule answer "what covers this
            // moment?" gives one owner and one answer, and the cue starts at its first word.
            //
            // The element is seeked directly rather than through `playStart` so the schedule's
            // very next tick already reads the new position; `playStart` still follows for the
            // waveform and the rest of the interface.
            seekOriginalTo(oneTrack.start);
            announceUserSeek();
            handles.setPlayStart(oneTrack.start);
            handles.setPlaying(true);
            // Глушить ли оригинал — теперь настройка (тумблер «Оригинал при клике» в футере),
            // а Ctrl/Cmd по-прежнему разово меняет её на противоположную: раньше это был
            // единственный способ, и о нём нужно было знать.
            const muteOriginal = e?.ctrlKey || e?.metaKey
                ? !state.muteOriginalOnVoicingClick
                : state.muteOriginalOnVoicingClick;
            handles.setVoiceoverOnlyPlayback(muteOriginal);
            // Гасим невыработанный "arm" от кликов по левой колонке, иначе
            // useAudioPlay переиграет эту часть после её окончания (playing теперь true)
            handles.setStartPlaying(false);
            handles.setPlaySavedTrack(false);
            handles["setZoomFragment"]([
                startY / zoomLevel,
                (startY + state.scrubberWidth) / zoomLevel,
            ]);
            handles.setPlaybackTime(oneTrack["start"]);
            handles["setPlayingFragmentNext"](null);
            handles.setLastActiveSubtitle({ playOriginalStart: oneTrack.original_start, playbackTime: oneTrack.start });
            handles.setLastActiveVoiceover({ trackId: oneTrack.id, playOriginalStart: oneTrack.original_start, playbackTime: oneTrack.start });
        };

        const getTrackClass = (trackId: string, tracks: AudioTrack[]) => {
            const { playingFragment, playingFragmentNext, changed } = state;
            // const playingFragment = id;

            let className = "";
            if (changed.includes(trackId)) className += " changed";
            if (trackId === playingFragment) className += " active";
            if (trimmed) className += " trimmed";
            if (state.changedRecently.includes(id)) className += " changed_recently";

            // Determine if this track is the next one to play and is currently in the delay period
            const isNextTrackInDelay =
                trackId === playingFragmentNext?.id && timeoutRef && timeoutRef.current;

            if (isNextTrackInDelay || trackId === handles.getNextTrackId(playingFragment, tracks)) {
                className += " next";
            }

            return className;
        };
        const dragHandleRef = useRef<HTMLDivElement | null>(null);
        const dragHandleRefStart = useRef<HTMLDivElement | null>(null);
        const lengthHandleRef = useRef<HTMLDivElement | null>(null);

        const handlePositionMouseDown = (e: MouseEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (openModalText) {
                return;
            }

            if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
                setIsDragging(true);
                setStartX(e.clientY);
            }

            if (
                dragHandleRefStart.current &&
                dragHandleRefStart.current.contains(e.target as Node)
            ) {
                setIsLengthingUpper(true);
                setStartX(e.clientY);
            }

            if (
                dragHandleRef.current &&
                !dragHandleRef.current.contains(e.target as Node) &&
                !lengthHandleRef.current?.contains(e.target as Node) &&
                !dragHandleRefStart.current?.contains(e.target as Node)
            ) {
                clickHandler(e);
            }
        };

        const getTrackBounds = () => {
            if (state.disableMovementRestrictions) {
                return { min: 0, max: state.durationFull };
            }

            return getSelectedMaxMinLength({
                ind,
                tracks,
                durationFull: state.durationFull,
                selectedTracks: selectedTranslatedTrackIds,
                // «Без автоправок» лента рисует файлы как есть — наехавшая озвучка не должна
                // застревать на месте: отодвинуть её от соседки можно всегда.
                allowCurrentOverlap: state.manualOnly,
            });
        };

        const getLengthBounds = () => {
            if (state.disableMovementRestrictions) {
                return { min: 0, max: state.durationFull };
            }

            return getMaxLength({
                ind,
                tracks,
                durationFull: state.durationFull,
                allowCurrentOverlap: state.manualOnly,
            });
        };

        const handleMouseMove = useCallback(
            (e: PointerEvent) => {
                if (state.subtitleEditing) {
                    return;
                }

                e.preventDefault();
                if (isDragging || isLengthingUpper) {
                    const text = checkSelectedTracks({ selectedTranslatedTrackIds, ind });
                    if (text) {
                        setOpenModalText(text);
                        setIsDragging(false);
                        setIsLengthingUpper(false);
                        return;
                    }

                    const { min, max } = getTrackBounds();
                    const deltaX = e.clientY - startY;
                    const newPosition = position + deltaX * zoomLevel;
                    const newLengthPosition = lengthPosition - deltaX * zoomLevel;
                    if (
                        newPosition >= min &&
                        (isDragging ? lengthPosition : 0) + newPosition <= max &&
                        newLengthPosition > 0
                    ) {
                        setPosition(newPosition);
                        if (selectedTranslatedTrackIds.length) {
                            setPositionDiff(deltaX * zoomLevel);
                        }
                        if (isLengthingUpper) {
                            if (
                                newPosition + newLengthPosition >= min &&
                                newPosition + newLengthPosition <= max
                            ) {
                                setLengthPosition(newLengthPosition);
                            }
                        }

                        setStartX(e.clientY);
                    }
                }
                if (isLengthing) {
                    const { min, max } = getLengthBounds();

                    const deltaX = e.clientY - lengthX;
                    const newLengthPosition = lengthPosition + deltaX * zoomLevel;
                    if (newLengthPosition > 1 && position + newLengthPosition <= max) {
                        setLengthPosition(newLengthPosition);
                        setLengthX(e.clientY);
                    }
                }
            },
            [
                isDragging,
                tracks,
                isLengthing,
                position,
                lengthPosition,
                startY,
                lengthX,
                state.subtitleEditing,
                state.durationFull,
                state.disableMovementRestrictions,
                state.manualOnly,
                ind,
                selectedTranslatedTrackIds,
            ],
        );

        useEffect(() => {
            // Сохраняется только то, что сдвинули. Здесь стояло сравнение последней координаты
            // указателя (пиксели) с началом реплики (миллисекунды) — оно не совпадало никогда, и
            // нажатие на ручку без движения сохраняло реплику и раздвигало соседей.
            if (
                !isDragging &&
                startY &&
                voicingWasMoved({ position, start, lengthPosition, length }) &&
                !openModalText
            ) {
                setPositionDiff(0);
                if (!openModalText) {
                    setSelectedTranslatedTrackIds([]);
                }

                if (selectedTranslatedTrackIds.length) {
                    (async () => {
                        try {
                            const data = await handles.updateTranslatedTrackPositions({
                                diffPosition: position - start,
                                selectedList: selectedTranslatedTrackIds.map((item) => item.track),
                            });
                            if (data?.data?.results) {
                                data?.data.results.forEach((item) => {
                                    if (item.error) {
                                        notification.openErrorNotification(item.error);
                                        setRerenderTracksIds((prev) => [...prev, item.data.id]);
                                    }
                                });
                            }
                        } catch (error) { }
                    })();
                } else {
                    (async () => {
                        try {
                            const accepted = await handles.updatePosition({
                                id: oneTrack.original_id,
                                newPosition: position,
                                newLength: lengthPosition,
                                tracks,
                                editedLang: state.lang,
                            });
                            // Отказ провайдера (озвучка раньше начала папки): назад, на место из файла.
                            if (accepted === false) {
                                setPosition(start);
                                setLengthPosition(length);
                            }
                        } catch (error) {
                            console.error("Error updating position or length:", error);
                        }
                    })();
                }
            }
        }, [isDragging]);

        const handleMouseUp = (e: PointerEvent) => {
            e.preventDefault();
            setIsDragging(false);
            setIsLengthing(false);
            setIsLengthingUpper(false);
        };

        useEffect(() => {
            if (draggingTrack.current) {
                draggingTrack.current.addEventListener("pointermove", handleMouseMove);
                draggingTrack.current.addEventListener("pointerup", handleMouseUp);
            }
            return () => {
                if (draggingTrack.current) {
                    draggingTrack.current.removeEventListener("pointermove", handleMouseMove);
                    draggingTrack.current.removeEventListener("pointerup", handleMouseUp);
                }
            };
        }, [isDragging, isLengthing, handleMouseMove, handleMouseUp]);

        const handleTouchStart = (e: TouchEvent) => {
            if (dragHandleRef.current && dragHandleRef.current.contains(e.target as Node)) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
                setStartX(e.touches[0].clientY);
            } else if (
                dragHandleRefStart.current &&
                dragHandleRefStart.current.contains(e.target as Node)
            ) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
                setIsLengthingUpper(true);
                setStartX(e.touches[0].clientY);
            }
            // If the touch is outside the drag handles, do nothing, allowing parent scroll
        };

        const handleTouchMove = useCallback(
            (e: TouchEvent) => {
                if (state.subtitleEditing) {
                    return;
                }
                if (isDragging || isLengthingUpper) {
                    e.preventDefault();
                    e.stopPropagation();
                    const text = checkSelectedTracks({ selectedTranslatedTrackIds, ind });
                    if (text) {
                        setOpenModalText(text);
                        setIsDragging(false);
                        setIsLengthingUpper(false);
                        return;
                    }
                    const touch = e.touches[0];
                    const { min, max } = getTrackBounds();
                    const deltaX = touch.clientY - startY;
                    const newPosition = position + deltaX * zoomLevel;
                    const newLengthPosition = lengthPosition - deltaX * zoomLevel;

                    if (
                        newPosition >= min &&
                        (isDragging ? lengthPosition : 0) + newPosition <= max &&
                        newLengthPosition > 0
                    ) {
                        setPosition(newPosition);
                        if (selectedTranslatedTrackIds.length) {
                            setPositionDiff(deltaX * zoomLevel);
                        }
                        if (isLengthingUpper) {
                            if (
                                newPosition + newLengthPosition >= min &&
                                newPosition + newLengthPosition <= max
                            ) {
                                setLengthPosition(newLengthPosition);
                            }
                        }

                        setStartX(touch.clientY);
                    }
                }
                // If not dragging, do nothing, allowing parent scroll
            },
            [
                isDragging,
                position,
                lengthPosition,
                startY,
                isLengthingUpper,
                state.subtitleEditing,
                state.durationFull,
                state.disableMovementRestrictions,
                state.manualOnly,
                selectedTranslatedTrackIds,
                ind,
                tracks,
            ],
        );

        const handleTouchEnd = (e: TouchEvent) => {
            if (isDragging) {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                setIsLengthingUpper(false);
            }
        };

        useEffect(() => {
            if (draggingTrack.current) {
                draggingTrack.current.addEventListener("touchstart", handleTouchStart, {
                    passive: false,
                });
                document.addEventListener("touchmove", handleTouchMove, { passive: false });
                document.addEventListener("touchend", handleTouchEnd);
            }
            return () => {
                if (draggingTrack.current) {
                    draggingTrack.current.removeEventListener("touchstart", handleTouchStart);
                }
                document.removeEventListener("touchmove", handleTouchMove);
                document.removeEventListener("touchend", handleTouchEnd);
            };
        }, [handleTouchMove, handleTouchEnd]);

        // Озвучка без реплики оригинала (4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026):
        // русскую реплику удалили, а испанский перевод и wav остались. Строки у неё нет — строки
        // строятся по оригиналу, — и раньше она была обычным блоком: звучала, а флажок рядом
        // выбирал её лишь для общей озвучки и скорости, удалить её было нечем. Теперь она молчит
        // (voicingSilenced), обведена красным пунктиром, говорит, что это, и удаляется 🗑 —
        // провайдер спросит, назвав время и текст.
        if (oneTrack.orphan_voicing === true) {
            const deleteTitle = orphanDeleteTitle(en);
            return (
                <TrackOrphanSC
                    data-orphan="true"
                    title={orphanVoicingHint(state.originalLang, en)}
                    top={`${position / zoomLevel}px`}
                    height={`${Math.max(lengthPosition / zoomLevel, FRAGMENT_MIN_HEIGHT)}px`}
                    layer={2}
                    right={"true"}
                >
                    <CueTime
                        localMs={oneTrack["start_rus_milli"]}
                        globalMs={toGlobalMs(oneTrack["start_rus_milli"], folderOffsetMs)}
                        showGlobal={state.showGlobalTime}
                        side="right"
                        gapToNextPx={gapToNextPx}
                    />
                    <OrphanTextSC>⚠ {oneTrack.captions}</OrphanTextSC>
                    <OrphanDeleteSC
                        type="button"
                        data-orphan-delete="true"
                        title={deleteTitle}
                        aria-label={deleteTitle}
                        onMouseDown={(e: MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            void handles.deleteOrphanVoicing(oneTrack);
                        }}
                    >
                        🗑
                    </OrphanDeleteSC>
                </TrackOrphanSC>
            );
        }

        // Снятая с озвучки (⊘) — пустое место, как реплика без озвучки: новый сервер оставляет на
        // записи её старый `file`, но в видео этого дубля нет, и колонка не рисует и не грузит его
        // (раньше у записи-пропуска `file` не было — место и было пустым). Почему пусто — словами.
        const skippedCue = oneTrack.skip === true;
        if (!voicingSounds(oneTrack)) {
            return (
                <TrackEmptySC
                    data-skipped={skippedCue ? "true" : undefined}
                    title={
                        skippedCue
                            ? en
                                ? "This cue is removed from voicing (⊘): its voicing will not be in the video and does not play in the editor."
                                : "Реплика снята с озвучки (⊘): её озвучка не попадёт в видео и в редакторе не звучит."
                            : undefined
                    }
                    top={`${position / zoomLevel}px`}
                    height={`${Math.max(lengthPosition / zoomLevel, FRAGMENT_MIN_HEIGHT)}px`}
                    shadow={
                        trimmed_start
                            ? `inset ${trimmed_start / zoomLevel}px 0 0 0px rgba(255, 0, 0, 0.7)`
                            : ""
                    }
                    layer={`${trimmed ? 1 : "inherit"}`}
                    right={"true"}
                    className={getTrackClass(oneTrack.id, tracks)}
                />
            );
        }

        // «Без автоправок»: наехавший хвост (его снимет следующая правка в папке — сдвигом
        // следующей позже) — красным и с подсказкой; поверх соседки, чтобы его не закрыла
        // следующая озвучка. Без режима — null, всё как было.
        const overlapLook = manualOverlapLook({
            manualOnly: state.manualOnly,
            overlapMs: oneTrack.manual_overlap_ms,
            lengthMs: lengthPosition,
            zoomLevel,
            en,
        });
        // Озвучка с испорченным временем (длина ≤ 0 — расписание её никогда не выберет, или сервер
        // пометил timing_invalid) обведена красным и объясняет себя: 30.09 такая озвучка выглядела
        // обычной, скачивалась и молчала.
        const brokenTime = oneTrack.timing_invalid === true || !(lengthPosition > 0);
        // Что испорчено — словами сервера; совет — по делу: длину дубля пересчитывает
        // переозвучка, а «вернуть окно» (на метке слева) нужно, только если сломано само окно.
        const brokenWhat = oneTrack.timing_problem || `${en ? "length" : "длина"} ${Math.round(lengthPosition)} ${en ? "ms" : "мс"}`;
        const brokenHint = brokenTime
            ? en
                ? `This voicing's time is broken (${brokenWhat}): the player skips it. Voice it again (♪) — the synthesis recomputes the length; if the mark on the left offers «restore window», restore it first.`
                : `Время этой озвучки испорчено (${brokenWhat}): плеер её пропускает. Переозвучьте её (♪) — синтез пересчитает длину; если метка слева предлагает «вернуть окно» — сначала верните окно.`
            : "";

        return (
            <TrackSC
                top={`${position / zoomLevel}px`}
                height={`${Math.max(lengthPosition / zoomLevel, FRAGMENT_MIN_HEIGHT)}px`}
                shadow={
                    brokenTime
                        ? "inset 0 0 0 2px rgba(255, 0, 0, 0.9)"
                        : overlapLook
                          ? overlapLook.shadow
                          : trimmed_start
                            ? `inset ${trimmed_start / zoomLevel}px 0 0 0px rgba(255, 0, 0, 0.7)`
                            : ""
                }
                layer={`${overlapLook ? 2 : trimmed ? 1 : "inherit"}`}
                right={"true"}
                className={getTrackClass(oneTrack.id, tracks)}
                onMouseDown={handlePositionMouseDown}
                // Дорожка озвучки реплики: её длина — длина дубля (ответ ♪, потом длина сервера), место — место дубля.
                // По этим полям проверка видит, что на ленте (проба P50m10e, E6: агент мерил строку перевода в
                // колонке оригинала — её высота — слот русской реплики, от дубля она не меняется).
                data-voicing-cue={oneTrack.original_id}
                data-length-ms={Math.round(lengthPosition)}
                data-start-ms={Math.round(position)}
                data-section={sectionState}
                data-manual-overlap-ms={overlapLook ? oneTrack.manual_overlap_ms : undefined}
                data-broken-time={brokenTime ? "true" : undefined}
                title={[brokenHint, overlapLook?.hint, sectionHint].filter(Boolean).join("\n")}
            >
                <SectionMarkSC state={sectionState} />
                <ClipMark id={oneTrack.id} />
                <CueTime
                    localMs={oneTrack["start_rus_milli"]}
                    globalMs={toGlobalMs(oneTrack["start_rus_milli"], folderOffsetMs)}
                    showGlobal={state.showGlobalTime}
                    side="right"
                    gapToNextPx={gapToNextPx}
                />
                <HorizontalLineSC />

                <div>
                    {state.waitingCut.includes(oneTrack.original_id) && (
                        <div className="p_b_preloader">
                            <img
                                src="https://assets-global.website-files.com/651f0451acd1cf1eb674532d/657223dc690f13a86f06e953_preloader.gif"
                                loading="lazy"
                                className="preloadergif"
                            />
                        </div>
                    )}

                    <div
                        style={{
                            overflow: "hidden",
                            // Отрицательную высоту браузер игнорирует — и волна вылезала за блок.
                            height: Math.max(lengthPosition / zoomLevel, 0),
                            opacity: state.waitingCut.includes(oneTrack.original_id) ? 0.3 : 1,
                        }}
                    >
                        <AudioPlayerTrack
                            id={id}
                            ind={ind}
                            trimmed={trimmed}
                            length={length}
                            width={oneTrack["original_length"] / zoomLevel}
                            end_rus_milli={oneTrack["end_rus_milli"]}
                            handlePlayNext={handlePlayNext}
                            mp3={mp3}
                            start={oneTrack.start}
                            prevTrack={tracks[ind - 1]}
                        />
                    </div>

                    {!isLengthing && (
                        <div className="x_track_drag0 x_track_drag0--v" ref={dragHandleRef}>
                            <div
                                className="x_track_drag-handle x_track_drag-handle--v"
                                style={
                                    state.subtitleEditing
                                        ? { background: "#EBEBE4", cursor: "no-drop" }
                                        : undefined
                                }
                            >
                                <svg
                                    className="x_track_drag-icon"
                                    width={20}
                                    height={14}
                                    viewBox="0 0 20 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M7 1.2071V12.7929C7 13.2383 6.46143 13.4614 6.14645 13.1464L0.707108 7.7071C0.316584 7.31658 0.316585 6.68341 0.707108 6.29289L6.14645 0.853549C6.46143 0.538567 7 0.761651 7 1.2071ZM13 1.2071V12.7929C13 13.2383 13.5386 13.4614 13.8536 13.1464L19.2929 7.7071C19.6834 7.31658 19.6834 6.68341 19.2929 6.29289L13.8536 0.853549C13.5386 0.538567 13 0.761651 13 1.2071Z"
                                        fill="white"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
                <SelectWrapperSC
                    onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                >
                    <SelectCheckbox
                        selectedTranslatedTrackIds={selectedTranslatedTrackIds}
                        setSelectedTranslatedTrackIds={setSelectedTranslatedTrackIds}
                        track={oneTrack}
                        ind={ind}
                    />
                </SelectWrapperSC>
                <AudioEditWrapperSC
                    onMouseDown={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
                >
                    <AudioManage
                        lang={state.lang}
                        subtitleId={oneTrack.original_id}
                        voice={oneTrack.voice}
                        disabled={state.translatedSubtitleId === oneTrack.original_id}
                        currentSpeed={oneTrack.speed}
                    />
                </AudioEditWrapperSC>
                <HorizontalLineSC bottom={"true"} />
                <DefaultModal
                    open={!!openModalText}
                    title={"Warning"}
                    onCancel={() => setOpenModalText("")}
                    onOk={() => setOpenModalText("")}
                    body={openModalText}
                />
            </TrackSC>
        );
    },
);
